import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import { PageLoader } from '../components/LoadingSpinner';
import { LeagueFormatSelector, type LeagueFormatType } from '../components/LeagueFormatSelector';
import { useSharedPlayerData } from '../hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '../utils/sharedLeagueConfig';
import { PlayerFeedbackStorage } from '../utils/playerFeedbackStorage';
import { filterAndSortPlayers, nextSortState, paginatePlayers, type RankSortField } from '../utils/rankingsDisplay';
import { boardInsightSummary, type ScoringFormat } from '../utils/leagueInsights';
import { PageShell } from '../components/ui/PageShell';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { PlayerPhoto } from '../components/PlayerPhoto';
import type { Player } from '../types/player';
import type { VoteValue } from '../types/feedback';

export const PlayerRankings = () => {
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState('All');
  const [sortField, setSortField] = useState<keyof Player | 'rank' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isSuperflex, setIsSuperflex] = useState(false);
  const [leagueFormat, setLeagueFormat] = useState<LeagueFormatType>('standard' as LeagueFormatType);
  const [scoringFormat, setScoringFormat] = useState<ScoringFormat>('ppr');
  const [votingInProgress, setVotingInProgress] = useState<Set<string>>(new Set());
  const [mobileFeedbackEnabled, setMobileFeedbackEnabled] = useState(false);
  const [page, setPage] = useState(1);


  // Reset superflex when switching away from QB position
  useEffect(() => {
    if (positionFilter !== 'QB' && isSuperflex) {
      setIsSuperflex(false);
    }
  }, [positionFilter, isSuperflex]);

  // Sync superflex state with league format
  useEffect(() => {
    if (leagueFormat === 'superflex' || leagueFormat === '2qb') {
      setIsSuperflex(true);
    } else if (leagueFormat === 'standard') {
      setIsSuperflex(false);
    }
  }, [leagueFormat]);

  // Create shared league configuration
  const leagueConfig = useMemo(() => {
    return createSharedLeagueConfig({
      format: leagueFormat,
      scoringFormat,
      teamCount: 12,
      isSuperflex
    });
  }, [leagueFormat, scoringFormat, isSuperflex]);

  // Use shared player data system (no scaling for Player Rankings)
  const { players, isLoading: pageLoading, error: pageError } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: false,
    scoringFormat,
  });



  // Handle position filter changes with superflex logic
  const handlePositionChange = (position: string) => {
    setPositionFilter(position);
    // Keep superflex state when switching to QB, reset when switching away
    if (position !== 'QB') {
      setIsSuperflex(false);
    }
  };

  const positions = ['All', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

  const handleVote = async (playerId: string, value: VoteValue) => {
    if (!user) {
      toast.error('Please sign in to vote on player rankings');
      return;
    }

    const player = filteredAndSortedPlayers.find(p => p.id === playerId);
    const playerName = player?.name || 'Player';

    setVotingInProgress(prev => new Set(prev).add(playerId));

    // Simulate a brief delay for UX feedback
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      // Store vote in localStorage
      PlayerFeedbackStorage.storeVote(user.id, playerId, value, playerName);

      // Show success toast
      if (value === 1) {
        toast.success(`📈 Voted ${playerName} should be ranked higher!`, {
          duration: 3000,
          icon: '👍',
        });
      } else if (value === -1) {
        toast.success(`📉 Voted ${playerName} should be ranked lower!`, {
          duration: 3000,
          icon: '👎',
        });
      } else {
        toast.success(`✅ Voted ${playerName} is ranked about right!`, {
          duration: 3000,
          icon: '👌',
        });
      }

      // Log for debugging
      const totalVotes = PlayerFeedbackStorage.getTotalVoteCount();
      console.log(`Total feedback votes stored: ${totalVotes}`);

    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error('Failed to save your vote. Please try again.');
    } finally {
      setVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(playerId);
        return newSet;
      });
    }
  };

  const handleSort = (field: RankSortField) => {
    const next = nextSortState(sortField, sortDirection, field);
    setSortField(next.field);
    setSortDirection(next.direction);
  };

  const filteredAndSortedPlayers = filterAndSortPlayers(players, {
    searchQuery,
    positionFilter,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    setPage(1);
  }, [searchQuery, positionFilter, sortField, sortDirection, leagueFormat, scoringFormat]);

  const paged = paginatePlayers(filteredAndSortedPlayers, page);
  const visiblePlayers = paged.items;

  if (pageLoading) return (
    <PageLoader 
      pageName="Player Rankings"
      loadingText="Loading..."
    />
  );
  if (pageError) {
    return (
      <PageShell>
        <PageHeader title="Player Rankings" />
        <Surface className="mx-auto max-w-xl p-8 text-center">
          <p className="text-base text-zinc-200">Could not load the ranking board.</p>
          <p className="mt-2 text-sm text-zinc-400">{pageError}</p>
        </Surface>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Helmet>
        <title>Player Rankings | Fantasy Fiesta</title>
        <meta name="description" content="Weekly fantasy football player rankings with positional value and team context. Filter by position and sort by value or age." />
        <link rel="canonical" href="https://fantasy-fiesta.com/players" />
      </Helmet>
      <PageHeader title="Player Rankings" />
      <Surface className="mx-auto mb-6 w-fit max-w-3xl px-5 py-2 text-center text-sm text-zinc-300">
        {boardInsightSummary(leagueFormat, scoringFormat)}
      </Surface>
      <div className="mb-6">
        <div className="hidden sm:block mb-4 space-y-3">
          <LeagueFormatSelector
            selectedFormat={leagueFormat}
            onFormatChange={setLeagueFormat}
            variant="pills"
          />
          <div className="flex flex-wrap items-center justify-center gap-2">
            {([
              { id: 'ppr', label: 'PPR' },
              { id: 'half-ppr', label: 'Half PPR' },
              { id: 'standard', label: 'Standard' },
            ] as const).map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setScoringFormat(option.id)}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  scoringFormat === option.id
                    ? 'border-fiesta-blue bg-fiesta-blue/15 text-fiesta-blue'
                    : 'border-white/10 bg-white/5 text-zinc-300 hover:border-white/20'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
          <input
            type="text"
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:flex-grow p-3 rounded-xl bg-white/5 text-white border border-white/10 focus:ring-2 focus:ring-fiesta-yellow/60 focus:border-transparent"
          />
          {/* Desktop/Tablet position chips (match mobile style for clarity) */}
          <div className="hidden sm:flex flex-nowrap gap-2 items-center">
            {positions.map((pos) => (
              <button
                key={`desktop-${pos}`}
                type="button"
                onClick={() => handlePositionChange(pos)}
                aria-pressed={positionFilter === pos}
                className={
                  `px-3 py-2 rounded-full border text-sm whitespace-nowrap transition-colors ` +
                  (positionFilter === pos
                    ? 'bg-fiesta-yellow text-ink border-fiesta-yellow'
                    : 'bg-white/5 text-zinc-200 border-white/10 hover:border-white/20')
                }
              >
                {pos === 'DEF' ? 'D/ST' : pos}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile League Format Selector - Dropdown */}
        <div className="sm:hidden mt-4 mb-3 space-y-3">
          <LeagueFormatSelector
            selectedFormat={leagueFormat}
            onFormatChange={setLeagueFormat}
            variant="dropdown"
          />
          <div className="flex flex-wrap justify-center gap-2">
            {([
              { id: 'ppr', label: 'PPR' },
              { id: 'half-ppr', label: 'Half PPR' },
              { id: 'standard', label: 'Standard' },
            ] as const).map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setScoringFormat(option.id)}
                className={`rounded-full border px-3 py-1.5 text-xs ${
                  scoringFormat === option.id
                    ? 'border-fiesta-blue bg-fiesta-blue/15 text-fiesta-blue'
                    : 'border-white/10 bg-white/5 text-zinc-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Mobile position chips */}
        <div className="mt-3 sm:hidden">
          <div className="text-neutral-400 text-sm mb-2 text-center">Filter by position</div>
          <div className="flex flex-wrap gap-1.5 py-1 justify-center">
            {positions.map((pos) => (
              <button
                key={pos}
                type="button"
                onClick={() => handlePositionChange(pos)}
                aria-pressed={positionFilter === pos}
                className={
                  `px-2 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap transition-colors ` +
                  (positionFilter === pos
                    ? 'bg-fiesta-yellow text-ink border-fiesta-yellow'
                    : 'bg-white/5 text-zinc-200 border-white/10 hover:border-white/20')
                }
              >
                {pos === 'DEF' ? 'D/ST' : pos}
              </button>
            ))}
          </div>
        </div>
      </div>




      {filteredAndSortedPlayers.length === 0 && !pageLoading ? (
        <div className="text-center py-10 text-neutral-400">No players match your criteria.</div>
      ) : (
        <>
          {/* Mobile Feedback Toggle */}
          <div className="lg:hidden mb-4">
            <label className="flex items-center space-x-3 p-3 bg-white/5 rounded-xl border border-white/10">
              <input
                type="checkbox"
                checked={mobileFeedbackEnabled}
                onChange={(e) => setMobileFeedbackEnabled(e.target.checked)}
                className="w-4 h-4 text-fiesta-yellow bg-zinc-700 border-zinc-600 rounded focus:ring-fiesta-yellow focus:ring-2"
              />
              <span className="text-sm text-neutral-200">Provide feedback on player rankings</span>
            </label>
          </div>

          {/* Mobile Card Layout */}
          <div className="lg:hidden space-y-3">
            {visiblePlayers.map((player: Player) => (
              <div key={player.id} className="bg-white/[0.03] rounded-xl p-4 border border-white/10 hover:border-fiesta-yellow/40 transition-colors duration-150">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="h-8 min-w-[2rem] px-2 bg-white/10 rounded-full inline-flex items-center justify-center text-xs font-semibold text-fiesta-yellow whitespace-nowrap">
                        #{player.rank}
                      </div>
                    </div>
                    <PlayerPhoto
                      name={player.name}
                      position={player.position}
                      team={player.team}
                      sleeperId={player.id}
                      espnId={player.espn_id}
                      imageUrl={player.imageUrl}
                      className="w-12 h-12 rounded-full object-cover bg-neutral-700 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-base truncate">{player.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          player.position === 'QB' ? 'bg-red-900 text-red-200' :
                          player.position === 'RB' ? 'bg-green-900 text-green-200' :
                          player.position === 'WR' ? 'bg-blue-900 text-blue-200' :
                          player.position === 'TE' ? 'bg-yellow-900 text-yellow-200' :
                          player.position === 'DEF' ? 'bg-gray-900 text-gray-200' :
                          'bg-gray-900 text-gray-200'
                        }`}>
                          {player.position === 'DEF' ? 'D/ST' : player.position}
                        </span>
                        <span className="text-sm text-neutral-300">{player.team}</span>
                        <span className="text-sm text-neutral-400">Age {player.age}</span>
                      </div>
                      {player.insight ? (
                        <p className="mt-2 text-xs leading-relaxed text-zinc-400">{player.insight}</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-zinc-400">
                        <span>Proj {Math.round(player.projection || player.projectedPoints || 0)}</span>
                        <span>Last year {Math.round(player.lastYearPoints || 0)}</span>
                        <span>VORP {(player.vorp || 0).toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Feedback Buttons */}
                {mobileFeedbackEnabled && (
                  <div className="flex items-center justify-center space-x-4 pt-3 border-t border-neutral-700">
                    <button
                      onClick={() => handleVote(player.id, 1)}
                      disabled={votingInProgress.has(player.id)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-neutral-700 hover:bg-green-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      <span className="text-sm text-green-400">Higher</span>
                    </button>
                    <button
                      onClick={() => handleVote(player.id, -1)}
                      disabled={votingInProgress.has(player.id)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-neutral-700 hover:bg-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      <span className="text-sm text-red-400">Lower</span>
                    </button>
                  </div>
                )}

              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <Surface className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-white/[0.03]">
                <tr>
                  {[
                    { key: 'rank', label: 'Rank', sortable: true, align: 'center' },
                    { key: 'name', label: 'Player', sortable: true, align: 'left' },
                    { key: 'position', label: 'Pos', sortable: true, align: 'center' },
                    { key: 'projection', label: 'Proj', sortable: true, align: 'center' },
                    { key: 'lastYearPoints', label: 'Last year', sortable: true, align: 'center' },
                    { key: 'vorp', label: 'VORP', sortable: true, align: 'center' },
                    { key: 'feedback', label: 'Feedback', sortable: false, align: 'center' }
                  ].map(({ key, label, sortable = true, align = 'left' }) => (
                    <th
                      key={key}
                      scope="col"
                      className={`px-4 py-3 text-${align} text-xs font-medium tracking-wide text-zinc-400 ${
                        sortable ? 'cursor-pointer hover:bg-white/5 transition-colors' : ''
                      }`}
                      onClick={sortable ? () => handleSort(key as RankSortField) : undefined}
                    >
                      <div className={`flex items-center ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'} space-x-1`}>
                        {key === 'feedback' ? (
                          <div className="flex items-center justify-center space-x-1">
                            <span>{label}</span>
                            <div className="group relative">
                              <svg
                                className="w-4 h-4 text-neutral-400 hover:text-neutral-200 cursor-help"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-white text-black text-xs rounded shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none max-w-48 normal-case tracking-tight" style={{ zIndex: 9999 }}>
                                <div className="text-center normal-case tracking-tight">Help shape rankings with your feedback</div>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-white"></div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span>{label}</span>
                            {sortable && (
                              <span className="text-neutral-500">
                                {sortField === key ? (
                                  sortDirection === 'asc' ? '↑' : '↓'
                                ) : (
                                  '↕'
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {visiblePlayers.map((player: Player) => (
                  <tr key={player.id} className="hover:bg-white/[0.03] transition-colors duration-150">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-neutral-100 text-center">{player.rank}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-100">
                      <div className="flex items-center">
                        <PlayerPhoto
                          name={player.name}
                          position={player.position}
                          team={player.team}
                          sleeperId={player.id}
                          espnId={player.espn_id}
                          imageUrl={player.imageUrl}
                          className="w-10 h-10 rounded-full mr-3 object-cover bg-neutral-700"
                        />
                        <div>
                          <div>{player.name}</div>
                          <div className="text-xs text-zinc-500">
                            {player.team}{player.insight ? ` · ${player.insight}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300 text-center">{player.position === 'DEF' ? 'D/ST' : player.position}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300 text-center">{Math.round(player.projection || player.projectedPoints || 0)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-neutral-300 text-center">{Math.round(player.lastYearPoints || 0)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-fiesta-yellow text-center">{(player.vorp || 0).toFixed(1)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleVote(player.id, 1)}
                          disabled={votingInProgress.has(player.id)}
                          className="p-1 rounded-full bg-neutral-700 hover:bg-green-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="This player should be ranked higher"
                        >
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleVote(player.id, -1)}
                          disabled={votingInProgress.has(player.id)}
                          className="p-1 rounded-full bg-neutral-700 hover:bg-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="This player should be ranked lower"
                        >
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Surface>

          {paged.pageCount > 1 ? (
            <div className="mt-6 flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-sm text-zinc-400">
                {paged.start} to {paged.end} of {paged.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={paged.page <= 1}
                  className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-200 disabled:cursor-not-allowed disabled:text-zinc-600"
                >
                  Previous
                </button>
                <span className="px-2 text-sm text-zinc-400">
                  Page {paged.page} of {paged.pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.min(paged.pageCount, current + 1))}
                  disabled={paged.page >= paged.pageCount}
                  className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-200 disabled:cursor-not-allowed disabled:text-zinc-600"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </PageShell>
  );
};